import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { mockVideos } from "@/mocks/videos";
import { articleContents, detailFallbackContent } from "@/mocks/content";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const article = mockVideos.find((v) => v.slug === id);
  const currentCategory = mockVideos.find((v) => v.slug === id)?.category;
  const relatedArticles = mockVideos
    .filter((v) => v.slug !== id && v.category === currentCategory)
    .slice(0, 4);

  if (!article) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 text-xl font-bold text-foreground-900">视频未找到</h1>
          <p className="mt-2 text-foreground-500">抱歉，您要查看的视频不存在或已被移除。</p>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-medium px-6 py-3 rounded-full text-sm transition-all">
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </main>
    );
  }

  const content = articleContents[article.slug] || detailFallbackContent;

  const categoryMap: Record<string, string> = {
    hot: "热门吃瓜",
    entertainment: "娱乐圈",
    social: "社会热点",
    emotion: "情感八卦",
    funny: "搞笑趣闻",
    bizarre: "奇闻异事",
  };

  return (
    <main className="pt-16 bg-background-50">
      <article>
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-4 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors">
                {categoryMap[article.category] || article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次观看
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> 阅读约需 3 分钟
              </span>
            </div>
            {article.tags && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-secondary-700 bg-secondary-100 px-2 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 flex flex-col lg:flex-row gap-8">
          <div className="flex-1 min-w-0">
            <div className="relative aspect-video rounded-xl overflow-hidden mb-8 bg-background-200">
              <img
                src={article.thumbnail}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-3 left-3 bg-black/65 backdrop-blur-sm text-white text-xs px-2 py-1 rounded font-medium">
                {article.duration}
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed [&>p]:mb-4 [&>p]:text-[15px] [&>p]:leading-7"
              dangerouslySetInnerHTML={{ __html: content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/60 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowShare(!showShare)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm text-foreground-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-share-forward-line"></i> 分享
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm text-foreground-700 transition-colors whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i> 点赞
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-sm text-foreground-700 transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
              </div>

              {showShare && (
                <div className="flex items-center gap-2 bg-background-50 border border-background-200/60 rounded-full px-3 py-2">
                  <span className="text-sm text-foreground-600 flex items-center gap-1 cursor-pointer hover:text-primary-500 whitespace-nowrap">
                    <i className="ri-wechat-line text-lg"></i> 微信
                  </span>
                  <span className="text-sm text-foreground-600 flex items-center gap-1 cursor-pointer hover:text-primary-500 whitespace-nowrap">
                    <i className="ri-weibo-line text-lg"></i> 微博
                  </span>
                  <span className="text-sm text-foreground-600 flex items-center gap-1 cursor-pointer hover:text-primary-500 whitespace-nowrap">
                    <i className="ri-links-line text-lg"></i> 复制链接
                  </span>
                </div>
              )}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <div className="w-1 h-4 bg-accent-500 rounded-full"></div>
                  相关推荐
                </h3>
                <div className="space-y-3">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors group"
                    >
                      <div className="relative w-28 flex-shrink-0 aspect-video rounded-lg overflow-hidden">
                        <img
                          src={rel.thumbnail}
                          alt={rel.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <span className="absolute bottom-1 right-1 bg-black/65 text-white text-[10px] px-1 rounded">
                          {rel.duration}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{rel.views.toLocaleString()} 次观看</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          )}
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.description,
            "image": article.thumbnail,
            "datePublished": article.date,
            "author": { "@type": "Organization", "name": "91吃瓜" },
            "publisher": { "@type": "Organization", "name": "91吃瓜", "url": "https://hnxyljy.cn/" },
          }),
        }}
      />
    </main>
  );
}